import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { workspaceSharingService } from "@/services/workspaceSharingService";
import { Copy, Check, Share2, X } from "lucide-react";
import type { WorkspaceShare } from "@shared/schema";

interface ShareWorkspaceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  workspaceName: string;
}

export function ShareWorkspaceDialog({ open, onOpenChange, workspaceId, workspaceName }: ShareWorkspaceDialogProps) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [accessType, setAccessType] = useState<"restricted" | "anyone" | "organization">("restricted");
  const [defaultMemberRole, setDefaultMemberRole] = useState<"editor" | "viewer">("viewer");

  const { data: existingShare, refetch: refetchShare } = useQuery<WorkspaceShare | null>({
    queryKey: ["/api/workspaces", workspaceId, "share"],
    queryFn: async () => {
      return await workspaceSharingService.getShareLink(workspaceId);
    },
    enabled: open && !!workspaceId,
  });

  const createShareMutation = useMutation({
    mutationFn: async (overrides?: {
      defaultMemberRole?: "editor" | "viewer";
      accessType?: "restricted" | "anyone" | "organization";
    }) => {
      return await workspaceSharingService.createShareLink({
        workspaceId,
        accessType: overrides?.accessType ?? accessType,
        defaultMemberRole: overrides?.defaultMemberRole ?? defaultMemberRole,
      });
    },
    onSuccess: () => {
      refetchShare();
      toast({
        title: "Share link created",
        description: "Your shareable link has been generated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create share link",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const revokeShareMutation = useMutation({
    mutationFn: async () => {
      return await workspaceSharingService.revokeShareLink(workspaceId);
    },
    onSuccess: () => {
      refetchShare();
      toast({
        title: "Share link revoked",
        description: "The share link has been deactivated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to revoke share link",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    },
  });

  const shareLink = existingShare
    ? `${window.location.origin}/workspaces/share/${existingShare.shareToken}`
    : null;

  const handleCopyLink = async () => {
    if (!shareLink) {
      toast({
        title: "No link available",
        description: "Please create a share link first",
        variant: "destructive",
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast({
        title: "Link copied!",
        description: "Share link has been copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: "Failed to copy",
        description: "Please copy the link manually",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    if (existingShare) {
      setAccessType(existingShare.accessType as "restricted" | "anyone" | "organization");
      const dm = (existingShare as { defaultMemberRole?: "editor" | "viewer" }).defaultMemberRole;
      if (dm === "editor" || dm === "viewer") setDefaultMemberRole(dm);
    }
  }, [existingShare]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share workspace
          </DialogTitle>
          <DialogDescription>
            Invite others to &quot;{workspaceName}&quot; with a link. Editors can change workspace content; viewers are read-only.
            Only content creators can delete their own items.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!shareLink ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ws-access-type">Access type</Label>
                <Select value={accessType} onValueChange={(v) => setAccessType(v as "restricted" | "anyone" | "organization")}>
                  <SelectTrigger id="ws-access-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">Restricted (invite only)</SelectItem>
                    <SelectItem value="anyone">Anyone with link</SelectItem>
                    <SelectItem value="organization">Organization only</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {accessType === "restricted" && "Users must accept the invitation to join the workspace"}
                  {accessType === "anyone" && "Anyone with the link can open the invite flow"}
                  {accessType === "organization" && "Only organization members can use the link"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ws-default-role">Default role for invited members</Label>
                <Select value={defaultMemberRole} onValueChange={(v) => setDefaultMemberRole(v as "editor" | "viewer")}>
                  <SelectTrigger id="ws-default-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Editors can update shared workspace content; viewers can only read.
                </p>
              </div>

              <Button
                onClick={() => createShareMutation.mutate(undefined)}
                disabled={createShareMutation.isPending}
                className="w-full"
                data-testid="button-create-workspace-share-link"
              >
                {createShareMutation.isPending ? "Creating..." : "Generate share link"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Shareable link</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={shareLink}
                    readOnly
                    className="flex-1 font-mono text-sm"
                    data-testid="input-workspace-share-link"
                  />
                  <Button variant="outline" size="icon" onClick={handleCopyLink} data-testid="button-copy-workspace-link">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">Copy this link and send it to teammates</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ws-access-type-edit">Access type</Label>
                <Select
                  value={accessType}
                  onValueChange={(v) => {
                    const next = v as "restricted" | "anyone" | "organization";
                    setAccessType(next);
                    createShareMutation.mutate({ accessType: next });
                  }}
                >
                  <SelectTrigger id="ws-access-type-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="restricted">Restricted (invite only)</SelectItem>
                    <SelectItem value="anyone">Anyone with link</SelectItem>
                    <SelectItem value="organization">Organization only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="ws-default-role-edit">Default role for invited members</Label>
                <Select
                  value={defaultMemberRole}
                  onValueChange={(v) => {
                    const role = v as "editor" | "viewer";
                    setDefaultMemberRole(role);
                    createShareMutation.mutate({ defaultMemberRole: role });
                  }}
                >
                  <SelectTrigger id="ws-default-role-edit">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="editor">Editor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => revokeShareMutation.mutate()}
                  disabled={revokeShareMutation.isPending}
                  className="flex-1"
                  data-testid="button-revoke-workspace-link"
                >
                  <X className="h-4 w-4 mr-2" />
                  {revokeShareMutation.isPending ? "Revoking..." : "Revoke link"}
                </Button>
                <Button onClick={handleCopyLink} className="flex-1" data-testid="button-copy-workspace-link-full">
                  <Copy className="h-4 w-4 mr-2" />
                  {copied ? "Copied!" : "Copy link"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
